import React from "react";
import { CodeBlock } from "./CodeBlock";

// A small, safe subset of Markdown for forum posts: paragraphs and line breaks,
// > quotes, - and 1. lists, ``` code blocks (```yaml highlights), `code`, **bold**, *italic*, and
// links ([text](https://…) or bare URLs). It builds React elements directly;
// nothing is ever injected as HTML, and only http(s) links are linked.

type Block =
  | { type: "p"; lines: string[] }
  | { type: "code"; text: string; lang: string | null }
  | { type: "quote"; text: string }
  | { type: "ul" | "ol"; items: string[] };

const QUOTE_RE = /^\s{0,3}>\s?/;
const UL_RE = /^\s{0,3}[-*•]\s+/;
const OL_RE = /^\s{0,3}\d{1,3}[.)]\s+/;

export function parseBlocks(text: string): Block[] {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*```/.test(line)) {
      const lang = line.trim().slice(3).trim().split(/\s+/)[0] || null;
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) body.push(lines[i++]);
      i++; // closing fence (or end)
      blocks.push({ type: "code", text: body.join("\n"), lang });
    } else if (QUOTE_RE.test(line)) {
      const body: string[] = [];
      while (i < lines.length && QUOTE_RE.test(lines[i])) body.push(lines[i++].replace(QUOTE_RE, ""));
      blocks.push({ type: "quote", text: body.join("\n") });
    } else if (UL_RE.test(line) || OL_RE.test(line)) {
      const re = UL_RE.test(line) ? UL_RE : OL_RE;
      const items: string[] = [];
      while (i < lines.length && re.test(lines[i])) items.push(lines[i++].replace(re, ""));
      blocks.push({ type: re === UL_RE ? "ul" : "ol", items });
    } else if (!line.trim()) {
      i++;
    } else {
      const body: string[] = [];
      while (
        i < lines.length &&
        lines[i].trim() &&
        !/^\s*```/.test(lines[i]) &&
        !QUOTE_RE.test(lines[i]) &&
        !UL_RE.test(lines[i]) &&
        !OL_RE.test(lines[i])
      ) {
        body.push(lines[i++]);
      }
      blocks.push({ type: "p", lines: body });
    }
  }
  return blocks;
}

const INLINE_RE =
  /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(\[[^\]\n]+\]\(https?:\/\/[^\s)]+\))|(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]])|((?<![\w*])\*[^*\n]+\*(?![\w*]))|((?<!\w)_[^_\n]+_(?!\w))/g;

const linkProps = { target: "_blank", rel: "nofollow ugc noopener noreferrer" };

function inline(text: string, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let n = 0;
  const re = new RegExp(INLINE_RE.source, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const idx = m.index;
    if (idx > last) out.push(text.slice(last, idx));
    const tok = m[0];
    const key = `${keyBase}-${n++}`;
    if (m[1]) out.push(<code key={key}>{tok.slice(1, -1)}</code>);
    else if (m[2]) out.push(<strong key={key}>{inline(tok.slice(2, -2), key)}</strong>);
    else if (m[3]) {
      const [, label, href] = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok) ?? [];
      out.push(
        <a key={key} href={href} {...linkProps}>
          {label}
        </a>,
      );
    } else if (m[4]) {
      out.push(
        <a key={key} href={tok} {...linkProps}>
          {tok.replace(/^https?:\/\/(www\.)?/, "")}
        </a>,
      );
    } else out.push(<em key={key}>{inline(tok.slice(1, -1), key)}</em>);
    last = idx + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function renderBlocks(blocks: Block[], keyBase = "b"): React.ReactNode[] {
  return blocks.map((b, i) => {
    const key = `${keyBase}${i}`;
    switch (b.type) {
      case "code":
        return <CodeBlock key={key} code={b.text} lang={b.lang} />;
      case "quote":
        return <blockquote key={key}>{renderBlocks(parseBlocks(b.text), `${key}q`)}</blockquote>;
      case "ul":
      case "ol": {
        const Tag = b.type;
        return (
          <Tag key={key}>
            {b.items.map((it, j) => (
              <li key={j}>{inline(it, `${key}-${j}`)}</li>
            ))}
          </Tag>
        );
      }
      default:
        return (
          <p key={key}>
            {b.lines.map((l, j) => (
              <React.Fragment key={j}>
                {j > 0 && <br />}
                {inline(l, `${key}-${j}`)}
              </React.Fragment>
            ))}
          </p>
        );
    }
  });
}

export const Markdown: React.FC<{ text: string; className?: string }> = ({ text, className }) => (
  <div className={`fm-md ${className ?? ""}`}>{renderBlocks(parseBlocks(text))}</div>
);

// Plain-text excerpt for lists (no markup, code collapsed).
export const stripMarkdown = (text: string) =>
  text
    .replace(/```[\s\S]*?(```|$)/g, " … ")
    .replace(/^\s{0,3}>\s?.*$/gm, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_`]/g, "")
    .replace(/^\s{0,3}([-•]|\d+[.)])\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
